import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import Select from 'components/UI/Select';
import Modal from 'components/Modal';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import { LOCAL_VISIT_STATUS } from 'constants/digitalLoanPhase3';
import {
  formatVisitDateDisplay,
  formatVisitTimeDisplay,
} from 'modules/digitalLoan/visitSchedule';
import { buildCappedLoanMessage } from '../utils/cappedLoanMessage';
import { formatDigitalLoanMoney } from '../utils/moneyInput';

const VisitStatusScreen = ({
  literals,
  snapshot = {},
  availableVisitDates = [],
  availableVisitTimeSlots = [],
  selectedVisitDate = '',
  selectedVisitTimeSlot = '',
  onVisitDateChange,
  onVisitTimeChange,
  onReschedule,
  onCancel,
  isSubmitting = false,
  canReschedule = false,
  canCancel = false,
  isWithinCutoff = false,
  maxReschedulesReached = false,
}) => {
  const [isModifying, setIsModifying] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);
  const [showRescheduleSuccess, setShowRescheduleSuccess] = useState(false);

  const isCancelled = snapshot.localVisitStatus === LOCAL_VISIT_STATUS.CANCELLED;
  const displayDate = isCancelled
    ? literals.emptyValue
    : formatVisitDateDisplay(snapshot.scheduledVisitAt);
  const displayTime = isCancelled
    ? literals.emptyValue
    : formatVisitTimeDisplay(snapshot.scheduledVisitAt);
  // TODO: replace inferred visit status with explicit
  // visitStatus/canReschedule/canCancel if Backend adds those fields.
  // TODO: copy especial si activeApplication.isException === true.

  const dateOptions = useMemo(
    () => availableVisitDates.map((date) => ({
      value: date,
      label: formatVisitDateDisplay(date),
    })),
    [availableVisitDates],
  );
  const timeOptions = useMemo(
    () => availableVisitTimeSlots.map((slot) => ({
      value: slot,
      label: slot,
    })),
    [availableVisitTimeSlots],
  );

  const showSchedulePicker = isCancelled || isModifying;
  const canConfirmNewSlot = !!selectedVisitDate && !!selectedVisitTimeSlot;

  const handleConfirmReschedule = async () => {
    if (!canConfirmNewSlot || isSubmitting) return;
    try {
      await onReschedule(selectedVisitDate, selectedVisitTimeSlot);
      setIsModifying(false);
      setShowRescheduleSuccess(true);
    } catch (error) {
      // El padre muestra ErrorModal; no cerrar el picker.
    }
  };

  const handleConfirmCancel = async () => {
    if (isSubmitting) return;
    try {
      await onCancel();
      setShowCancelConfirm(false);
      setShowCancelSuccess(true);
    } catch (error) {
      setShowCancelConfirm(false);
    }
  };

  const modifyCopy = (() => {
    if (isWithinCutoff && maxReschedulesReached) {
      return [literals.maxReschedulesReached, literals.withinCutoffHint];
    }
    if (isWithinCutoff) {
      return [literals.withinCutoffHint];
    }
    if (maxReschedulesReached) {
      return [literals.maxReschedulesReached];
    }
    return [literals.modifyHint];
  })();
  const cappedMessage = buildCappedLoanMessage(
    literals.cappedAmount,
    snapshot,
  );

  return (
    <div className='full-screen-container digital-loan-visit-status'>
      <div className='screen-content digital-loan-preoffer__scroll'>
        <h1 className='digital-loan-phase2__title digital-loan-preoffer__title'>
          {literals.title}
        </h1>
        <p className='digital-loan-phase2__body mb-4'>{literals.subtitle}</p>

        <div className='digital-loan-summary-block'>
          <h2 className='digital-loan-summary-block__title'>{literals.loanSection}</h2>
          <div className='digital-loan-summary-block__row'>
            <span className='digital-loan-summary-block__label'>{literals.amountLabel}</span>
            <strong>
              {formatDigitalLoanMoney(
                snapshot.acceptedAmount
                ?? snapshot.preapprovalAmount
                ?? snapshot.suggestedLoanAmount,
                '- -',
              )}
            </strong>
          </div>
          {cappedMessage && (
            <p className='digital-loan-phase2__body mt-2' role='status'>
              {cappedMessage}
            </p>
          )}
          <div className='digital-loan-summary-block__row'>
            <span className='digital-loan-summary-block__label'>{literals.termLabel}</span>
            <strong>
              {snapshot.requestedTerm || literals.emptyValue}
              {snapshot.requestedTerm ? ` ${literals.termUnit}` : ''}
            </strong>
          </div>
          <div className='digital-loan-summary-block__row'>
            <span className='digital-loan-summary-block__label'>{literals.frequencyLabel}</span>
            <strong>{snapshot.paymentFrequency || literals.emptyValue}</strong>
          </div>
        </div>

        <div className='digital-loan-summary-block'>
          <h2 className='digital-loan-summary-block__title'>{literals.visitSection}</h2>
          <div className='digital-loan-summary-block__row'>
            <span className='digital-loan-summary-block__label'>{literals.visitDayLabel}</span>
            <strong>{displayDate}</strong>
          </div>
          <div className='digital-loan-summary-block__row'>
            <span className='digital-loan-summary-block__label'>{literals.visitTimeLabel}</span>
            <strong>{displayTime}</strong>
          </div>
        </div>

        <div className='digital-loan-summary-block'>
          <h2 className='digital-loan-summary-block__title'>{literals.guaranteeSection}</h2>
          <div className='digital-loan-preoffer__guarantee'>
            <span>{snapshot.guaranteeName || literals.emptyValue}</span>
            {snapshot.guaranteeImage && (
              <img
                src={snapshot.guaranteeImage}
                alt={snapshot.guaranteeName || ''}
                className='digital-loan-preoffer__guarantee-img'
              />
            )}
          </div>
        </div>

        {!isCancelled && (
          <div className='digital-loan-summary-block'>
            <h2 className='digital-loan-summary-block__title'>
              {literals.modifySection}
            </h2>
            {modifyCopy.map((text) => (
              <p key={text} className='digital-loan-phase2__body mb-3'>
                {text}
              </p>
            ))}
          </div>
        )}

        {isCancelled && (
          <div className='digital-loan-summary-block'>
            <h2 className='digital-loan-summary-block__title'>
              {literals.scheduleAgainTitle}
            </h2>
          </div>
        )}

        {showSchedulePicker && (
          <>
            <div className='digital-loan-preoffer__visit-card mb-3'>
              <CalendarIcon className='digital-loan-preoffer__calendar' aria-hidden='true' />
              <Select
                className='digital-loan-preoffer__visit-select'
                name='visitDate'
                placeholder={literals.selectDay}
                options={dateOptions}
                value={selectedVisitDate || ''}
                onChange={onVisitDateChange}
                disabled={isSubmitting}
              />
            </div>
            {selectedVisitDate && (
              <div className='digital-loan-preoffer__visit-card mb-3'>
                <Select
                  className='digital-loan-preoffer__visit-select'
                  name='visitTime'
                  placeholder={literals.selectTime}
                  options={timeOptions}
                  value={selectedVisitTimeSlot || ''}
                  onChange={onVisitTimeChange}
                  disabled={isSubmitting}
                />
              </div>
            )}
          </>
        )}
      </div>

      <div className='screen-actions'>
        {!isCancelled && !isModifying && (
          <>
            <Button
              text={literals.modify}
              disabled={!canReschedule || isSubmitting}
              onClick={() => setIsModifying(true)}
            />
            <Button
              text={literals.cancel}
              type='secondary'
              disabled={!canCancel || isSubmitting}
              onClick={() => setShowCancelConfirm(true)}
            />
          </>
        )}
        {showSchedulePicker && (
          <Button
            text={literals.confirmReschedule}
            disabled={!canConfirmNewSlot || isSubmitting}
            loading={isSubmitting}
            onClick={handleConfirmReschedule}
          />
        )}
        {isModifying && (
          <Button
            text={literals.back}
            type='secondary'
            disabled={isSubmitting}
            onClick={() => setIsModifying(false)}
          />
        )}
      </div>

      {showRescheduleSuccess && (
        <Modal
          className='digital-loan-visit-modal'
          closeOnBackgroundClick={false}
          showCloseButton={false}
          onClose={() => setShowRescheduleSuccess(false)}
        >
          <h2 className='digital-loan-visit-modal__title'>
            {literals.rescheduleSuccessTitle}
          </h2>
          <p className='digital-loan-phase2__body'>{literals.rescheduleSuccessBody1}</p>
          <p className='digital-loan-phase2__body mb-3'>{literals.rescheduleSuccessBody2}</p>
          <p className='digital-loan-summary-block__row'>
            <span className='digital-loan-summary-block__label'>{literals.visitDayLabel}</span>
            <strong>{formatVisitDateDisplay(snapshot.scheduledVisitAt)}</strong>
          </p>
          <p className='digital-loan-summary-block__row mb-4'>
            <span className='digital-loan-summary-block__label'>{literals.visitTimeLabel}</span>
            <strong>{formatVisitTimeDisplay(snapshot.scheduledVisitAt)}</strong>
          </p>
          <Button
            text={literals.understood}
            onClick={() => setShowRescheduleSuccess(false)}
          />
        </Modal>
      )}

      {showCancelConfirm && (
        <Modal
          className='digital-loan-visit-modal'
          closeOnBackgroundClick={false}
          showCloseButton={false}
          onClose={() => setShowCancelConfirm(false)}
        >
          <h2 className='digital-loan-visit-modal__title'>
            {literals.cancelConfirmTitle}
          </h2>
          <p className='digital-loan-phase2__body'>{literals.cancelConfirmBody}</p>
          <p className='digital-loan-summary-block__row'>
            <span className='digital-loan-summary-block__label'>{literals.visitDayLabel}</span>
            <strong>{formatVisitDateDisplay(snapshot.scheduledVisitAt)}</strong>
          </p>
          <p className='digital-loan-summary-block__row mb-3'>
            <span className='digital-loan-summary-block__label'>{literals.visitTimeLabel}</span>
            <strong>{formatVisitTimeDisplay(snapshot.scheduledVisitAt)}</strong>
          </p>
          <p className='digital-loan-phase2__body mb-4'>{literals.cancelConfirmHint}</p>
          <Button text={literals.cancelConfirmCta} onClick={handleConfirmCancel} />
          <Button
            text={literals.back}
            type='secondary'
            onClick={() => setShowCancelConfirm(false)}
          />
        </Modal>
      )}

      {showCancelSuccess && (
        <Modal
          className='digital-loan-visit-modal'
          closeOnBackgroundClick={false}
          showCloseButton={false}
          onClose={() => setShowCancelSuccess(false)}
        >
          <h2 className='digital-loan-visit-modal__title'>
            {literals.cancelSuccessTitle}
          </h2>
          <p className='digital-loan-phase2__body mb-4'>{literals.cancelSuccessBody}</p>
          <Button
            text={literals.understood}
            onClick={() => setShowCancelSuccess(false)}
          />
        </Modal>
      )}
    </div>
  );
};

VisitStatusScreen.propTypes = {
  literals: PropTypes.object.isRequired,
  snapshot: PropTypes.object,
  availableVisitDates: PropTypes.array,
  availableVisitTimeSlots: PropTypes.array,
  selectedVisitDate: PropTypes.string,
  selectedVisitTimeSlot: PropTypes.string,
  onVisitDateChange: PropTypes.func.isRequired,
  onVisitTimeChange: PropTypes.func.isRequired,
  onReschedule: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  canReschedule: PropTypes.bool,
  canCancel: PropTypes.bool,
  isWithinCutoff: PropTypes.bool,
  maxReschedulesReached: PropTypes.bool,
};

export default VisitStatusScreen;
